<script>
import {
  formatDateSeparator,
  parseBackendDate,
} from '@modules/atendimento/utils/message-formatter'
import ChatMessage from '@modules/atendimento/components/chat-message/chat-message.vue'
import ChatBadge from '@modules/atendimento/components/chat-badge/chat-badge.vue'
import apiAtendimento from '@modules/atendimento/api/apiAtendimento'
import { HISTORY_SCROLL } from '@modules/atendimento/constants/Text'

export default {
  name: 'ChatWindowMessages',
  components: { ChatMessage, ChatBadge },
  props: {
    mensagens: {
      type: Array,
      default: () => [],
    },
    loadingOlder: {
      type: Boolean,
      default: false,
    },
    // Label exibido ao lado do spinner de carregamento de antigos. Muda entre
    // "carregando do banco" e "buscando no WhatsApp" (import do Evolution).
    olderLoadingLabel: {
      type: String,
      default: '',
    },
    // Quando true, mostra o indicador discreto "Início da conversa" no topo
    // (banco e WhatsApp esgotados). Não aparece junto com o spinner.
    beginningOfConversation: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // M1 — mapa { [message_id]: id_task } que decide quais mensagens
    // mostram o badge de tarefa vinculada.
    linkedTaskByMessageId: {
      type: Object,
      default: () => ({}),
    },
    // Seams repassados ao ChatMessage compartilhado — defaults = atendimento.
    api: {
      type: Object,
      default: () => apiAtendimento,
    },
    namespace: {
      type: String,
      default: 'atendimento',
    },
    // Feature flags opt-in (boolean default false; solicitacoes liga via hide-*).
    hideNote: {
      type: Boolean,
      default: false,
    },
    hideTask: {
      type: Boolean,
      default: false,
    },
    // Repassado ao ChatMessage: habilita "Enviar para AAWZ Drive" quando a
    // conversa tem cliente vinculado.
    hasLinkedClient: {
      type: Boolean,
      default: false,
    },
    // Cliente vinculado — repassado ao ChatMessage para o menu de Drive.
    idClienteApp: {
      type: [String, Number],
      default: null,
    },
    hidePhoneFallback: {
      type: Boolean,
      default: false,
    },
    // Opt-in repassado ao ChatMessage: indicador de canal por mensagem
    // (solicitacoes). Default false = atendimento sem badge de canal.
    showCanalBadge: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    beginningLabel() {
      return HISTORY_SCROLL.BEGINNING
    },
    messagesWithDateSeparators() {
      const msgs = this.mensagens || []
      if (!msgs.length) return []
      const result = []
      let lastDateKey = ''
      for (const m of msgs) {
        const d = parseBackendDate(m.data_mensagem)
        const dateKey = d
          ? d.getFullYear() + '-' + d.getMonth() + '-' + d.getDate()
          : ''
        if (dateKey && dateKey !== lastDateKey) {
          result.push({
            _isSeparator: true,
            _separatorId: 'sep-' + dateKey,
            _label: formatDateSeparator(m.data_mensagem),
          })
          lastDateKey = dateKey
        }
        result.push(m)
      }
      return result
    },
  },
  methods: {
    scrollToBottom() {
      // nextTick roda após o DOM update do Vue mas ANTES do navegador
      // medir o layout — em mensagens com mídia (sticker/imagem), o
      // scrollHeight ainda não reflete a altura final, então o scroll
      // ficava preso na posição anterior. Dois requestAnimationFrame
      // garantem ao menos um paint completo antes de medir.
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const container = this.$refs.scrollContainer
            if (container) {
              container.scrollTop = container.scrollHeight
            }
          })
        })
      })
    },
    onScroll(event) {
      this.$emit('scroll', event)
    },
    onRetry(mensagem) {
      this.$emit('retry', mensagem)
    },
    onReply(mensagem) {
      this.$emit('reply', mensagem)
    },
    onEdit(mensagem) {
      this.$emit('edit', mensagem)
    },
    onReact(payload) {
      this.$emit('react', payload)
    },
    onNote(mensagem) {
      this.$emit('note', mensagem)
    },
    onDelete(mensagem) {
      this.$emit('delete', mensagem)
    },
    onScrollToMessage(messageId) {
      this.$emit('scroll-to-message', messageId)
    },
    // Resolve o id da tarefa vinculada a uma mensagem (ou null/undefined).
    linkedTaskFor(messageId) {
      const map = this.linkedTaskByMessageId || {}
      return map[messageId] || null
    },
    onCreateTask(mensagem) {
      this.$emit('create-task', mensagem)
    },
    onSendToDrive(payload) {
      this.$emit('send-to-drive', payload)
    },
    onSendToDriveRename(payload) {
      this.$emit('send-to-drive-rename', payload)
    },
    onOpenTask(taskId) {
      this.$emit('open-task', taskId)
    },
  },
}
</script>

<template>
  <div
    ref="scrollContainer"
    class="chat-window__messages flex-grow-1 overflow-y-auto pa-3"
    @scroll="onScroll"
  >
    <!-- Loading skeleton (first load) -->
    <template v-if="loading && (!mensagens || !mensagens.length)">
      <div class="d-flex justify-center my-3">
        <BaseLoadingSkeleton width="80px" height="24px" class="rounded-pill" />
      </div>
      <div
        v-for="i in 6"
        :key="'msg-skeleton-' + i"
        class="d-flex mb-3"
        :class="i % 2 === 0 ? 'justify-start' : 'justify-end'"
      >
        <BaseLoadingSkeleton
          :width="120 + i * 20 + 'px'"
          height="48px"
          class="rounded-lg"
        />
      </div>
    </template>

    <template v-else>
      <output
        v-if="loadingOlder"
        class="d-flex flex-column align-center pa-2"
      >
        <v-progress-circular indeterminate size="24" width="2" color="grey" />
        <span
          v-if="olderLoadingLabel"
          class="az-text-small az-muted mt-1 text-center"
        >
          {{ olderLoadingLabel }}
        </span>
      </output>

      <output
        v-else-if="beginningOfConversation"
        class="d-block text-center pa-2"
      >
        <span class="az-text-small az-muted">
          {{ beginningLabel }}
        </span>
      </output>

      <template v-for="item in messagesWithDateSeparators">
        <div
          v-if="item._isSeparator"
          :key="item._separatorId"
          class="d-flex align-center justify-center my-3"
        >
          <ChatBadge variant="date" :text="item._label" />
        </div>
        <ChatMessage
          v-else
          :key="item.id"
          :mensagem="item"
          :linked-task-id="linkedTaskFor(item.id)"
          :api="api"
          :namespace="namespace"
          :hide-note="hideNote"
          :hide-task="hideTask"
          :has-linked-client="hasLinkedClient"
          :id-cliente-app="idClienteApp"
          :hide-phone-fallback="hidePhoneFallback"
          :show-canal-badge="showCanalBadge"
          :data-message-id="item.id"
          :data-whatsapp-id="item.whatsapp_message_id || ''"
          @retry="onRetry"
          @reply="onReply"
          @edit="onEdit"
          @react="onReact"
          @note="onNote"
          @delete="onDelete"
          @create-task="onCreateTask"
          @send-to-drive="onSendToDrive"
          @send-to-drive-rename="onSendToDriveRename"
          @open-task="onOpenTask"
          @scroll-to-message="onScrollToMessage"
        />
      </template>
    </template>
  </div>
</template>

<style lang="sass" scoped>
.chat-window__messages
  min-height: 0
  background-color: #ece5dd

  &::-webkit-scrollbar
    width: 6px

  &::-webkit-scrollbar-track
    background: #ece5dd

  &::-webkit-scrollbar-thumb
    background: #c4c5c7
    border-radius: 3px

  &::-webkit-scrollbar-thumb:hover
    background: #a0a1a3
</style>
